import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ITaskOrderReport } from "../../types/taskOrderReport.type";
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  message,
  Modal,
  Space,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import { UserRole } from "../../types/user.type";
import { useAuth } from "../../contexts/auth.context";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function TaskOrderReports() {
  const { id } = useParams();
  const { authState } = useAuth();
  const [reports, setReports] = useState<ITaskOrderReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchReports = async () => {
    setLoading(true);

    try {
      const response = await api.get(`/task-order/${id}/reports`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YYYY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YYYY") : undefined,
        },
      });

      setReports(response.data.reports);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching reports", error);
      message.error("Ошибка при получение отчетов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page, pageSize, dateRange]);

  const handleSubmit = async (values: { quantity: number }) => {
    setConfirmLoading(true);

    try {
      await api.post(`/task-order/${id}/report`, values);
      message.success("Отчёт успешно добавлен.");
    } catch (error: any) {
      console.error("Error saving report:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchReports();
      form.resetFields();
    }
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Продукция",
      dataIndex: "produceName",
      key: "produceName",
    },
    {
      title: "Кол-во",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Пользователь",
      dataIndex: "ownerName",
      key: "ownerName",
    },
  ];
  return (
    <div style={{ padding: 24 }}>
      <Title>Отчеты заказа</Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />

        {(authState.user?.role === UserRole.Admin ||
          authState.user?.role === UserRole.SalesDept) && (
          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            Добавить
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={reports}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
      />

      <Modal
        title="Добавить"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="quantity"
            label="Кол-во"
            rules={[{ required: true, message: "Пожалуйста, введите кол-во" }]}
          >
            <InputNumber style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
